import { useCreateUserAction } from "@/action/use-create-user-action";
import { GroupChipCombobox } from "@/components/group/GroupChipCombobox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useGlobalUsersData } from "@/data/use-users-data";
import { useWizard } from "@/hooks/useWizard";
import { cn } from "@/lib/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAdd,
  faArrowLeft,
  faArrowRight,
  faTrash,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Row } from "../ui/flex";
import { GroupRoleSelection, UserRoleSelection } from "./RoleSelection";
import {
  EmailTag,
  UserCreationProgress,
  UserRow,
  createUsersWithProgress,
  emailTagsToUsers,
  groupRoleOnSubmit,
  handleGroupSelection,
  handleUserCreationResults,
  initializeUserProgresses,
  parseEmailsFromText,
  validateUsers,
} from "./lib";

export function CreateUserButton({
  groupUuid,
}: {
  groupUuid?: string | null | undefined;
}) {
  const [emailInput, setEmailInput] = useState("");
  const [emailTags, setEmailTags] = useState<EmailTag[]>([]);
  const [users, setUsers] = useState<UserRow[]>([]);

  const [userProgresses, setUserProgresses] = useState<
    Map<string, UserCreationProgress>
  >(new Map());
  const [isProcessing, setIsProcessing] = useState(false);

  const { users: existingUsers } = useGlobalUsersData(!groupUuid);
  const { createUser } = useCreateUserAction();
  const navigate = useNavigate();

  const wizard = useWizard({
    steps: ["input", "create"],
    canNext: (stepIndex: number) => {
      if (stepIndex === 0) {
        return (
          emailTags.length > 0 &&
          emailTags.some((tag) => tag.groupUuids.length > 0)
        );
      }
      return true;
    },
    onStepChange: (stepIndex: number) => {
      if (stepIndex === 1) {
        convertToUsers();
      } else if (stepIndex === 0) {
        convertFromUsers();
      }
    },
  });

  const emails = useMemo(
    () =>
      groupUuid
        ? new Set<string>()
        : new Set(existingUsers.map((user) => user.email)),
    [existingUsers, groupUuid],
  );

  const handleEmailInputChange = (value: string) => {
    setEmailInput(value);
    const defaultGroups = groupUuid ? [groupUuid] : [];
    const parsed = parseEmailsFromText(value, defaultGroups);
    setEmailTags(parsed);
  };

  const handleGroupSelectionTags = (groupUuid: string, selected: boolean) => {
    setEmailTags((prev) =>
      prev.map((tag) => ({
        ...tag,
        groupUuids: selected
          ? [...tag.groupUuids.filter((uuid) => uuid !== groupUuid), groupUuid]
          : tag.groupUuids.filter((uuid) => uuid !== groupUuid),
      })),
    );
  };

  const convertToUsers = () => {
    setUsers(
      emailTagsToUsers(emailTags).map((user) =>
        groupUuid ? { ...user, groupRole: "member" as const } : user,
      ),
    );
  };

  const convertFromUsers = () => {
    const tags: EmailTag[] = users.map((user) => ({
      id: user.id,
      email: user.email,
      groupUuids: user.groupUuids,
    }));

    setEmailTags(tags);

    const emailText = users
      .map((user) => user.email)
      .filter(Boolean)
      .join(", ");
    setEmailInput(emailText);
  };

  const updateUser = (id: string, updates: Partial<UserRow>) => {
    setUsers((prev) =>
      prev.map((user) => (user.id === id ? { ...user, ...updates } : user)),
    );
  };

  const removeUser = (id: string) => {
    if (users.length > 1) {
      setUsers((prev) => prev.filter((user) => user.id !== id));
    }
  };

  const addUser = () => {
    const newUser: UserRow = {
      id: `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      email: "",
      role: "user",
      groupRole: groupUuid ? "member" : undefined,
      groupUuids: groupUuid ? [groupUuid] : [],
    };
    setUsers((prev) => [...prev, newUser]);
  };

  const updateUserProgress = (
    id: string,
    update: Partial<UserCreationProgress>,
  ) => {
    setUserProgresses((prev) => {
      const newMap = new Map(prev);
      const current = newMap.get(id) || {
        id,
        email: "",
        status: "pending" as const,
      };
      newMap.set(id, { ...current, ...update });
      return newMap;
    });
  };

  const handleCreate = async () => {
    const { validUsers, validationErrors } = validateUsers(users, emails, {
      allowExistingEmail: Boolean(groupUuid),
    });

    if (validUsers.length === 0) {
      toast.error("No valid users to create");
      return;
    }

    if (Object.keys(validationErrors).length > 0) {
      toast.error("Please fix validation errors before proceeding");
      return;
    }

    const usersToCreate = groupUuid
      ? validUsers.map((user) => ({
          ...user,
          groupRole: groupRoleOnSubmit(user.groupRole),
        }))
      : validUsers;

    setIsProcessing(true);
    setUserProgresses(initializeUserProgresses(usersToCreate));

    try {
      const { successful, failed } = await createUsersWithProgress(
        usersToCreate,
        createUser,
        updateUserProgress,
      );

      handleUserCreationResults(successful, failed, validUsers.length, toast);

      if (successful.length === validUsers.length) {
        resetState();
        if (!groupUuid && successful.length === 1 && successful[0].uuid) {
          navigate({
            to: "/admin/users/$userUuid",
            params: { userUuid: successful[0].uuid },
          });
        }
      }
    } catch {
      toast.error("User creation failed");
    } finally {
      setIsProcessing(false);
    }
  };

  const resetState = () => {
    wizard.goTo(0);
    setEmailInput("");
    setEmailTags([]);
    setUsers([]);
    setIsProcessing(false);
    setUserProgresses(new Map());
  };

  const { validationErrors } = validateUsers(
    wizard.currentStepIndex === 0 ? emailTagsToUsers(emailTags) : users,
    emails,
    { allowExistingEmail: Boolean(groupUuid) },
  );
  const hasErrors = Object.keys(validationErrors).length > 0;
  const hasValidUsers = users.some((user) => user.email.trim());

  return (
    <AlertDialog
      onOpenChange={(open) => {
        if (!open) {
          resetState();
        }
      }}
    >
      <AlertDialogTrigger asChild>
        <Button size="default" title="Create User">
          <FontAwesomeIcon icon={faAdd} />
          Create User
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="max-h-[80vh] max-w-4xl">
        <AlertDialogHeader className="flex-row items-center justify-between">
          <AlertDialogTitle>
            Create Users ({wizard.currentStepIndex + 1} of {wizard.totalSteps})
          </AlertDialogTitle>
          {wizard.currentStep === "create" && (
            <Button variant="ghost" size="sm" onClick={addUser}>
              <FontAwesomeIcon icon={faAdd} />
              Add user
            </Button>
          )}
        </AlertDialogHeader>
        <AlertDialogDescription>
          {wizard.currentStep === "input" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Email Addresses</label>
                <Textarea
                  placeholder="Enter email addresses separated by commas, spaces, or new lines..."
                  value={emailInput}
                  onChange={(e) => handleEmailInputChange(e.target.value)}
                  rows={4}
                  className="min-h-[100px]"
                />
              </div>

              {!groupUuid && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Groups</label>
                  <GroupChipCombobox
                    disabled={emailTags.length === 0}
                    selectedGroupUuids={
                      groupUuid
                        ? [groupUuid]
                        : emailTags.length
                          ? emailTags[0].groupUuids
                          : []
                    }
                    onChange={handleGroupSelectionTags}
                  />
                </div>
              )}
            </div>
          )}

          {wizard.currentStep === "create" && (
            <div className="space-y-4">
              <div className="max-h-[400px] space-y-2 overflow-y-auto">
                {users.map((user) => {
                  const progress = userProgresses.get(user.id);
                  const userErrors =
                    user.id in validationErrors
                      ? validationErrors[user.id]
                      : [];
                  const hasUserErrors = userErrors.length > 0;

                  return (
                    <div
                      key={user.id}
                      className={cn("space-y-2 rounded-lg border p-3", {
                        "border-red-200 bg-red-50":
                          (hasUserErrors && !progress) ||
                          progress?.status === "failed",
                      })}
                    >
                      <div
                        className={cn("grid grid-cols-[1fr_auto_auto] gap-2", {
                          "grid-rows-2": !groupUuid,
                          "items-center": groupUuid,
                        })}
                      >
                        <Input
                          placeholder="Email"
                          value={user.email}
                          onChange={(e) =>
                            updateUser(user.id, {
                              email: e.target.value.trim(),
                            })
                          }
                          disabled={isProcessing}
                          className={cn({
                            "border-red-500": userErrors.some(
                              (error) =>
                                error.includes("email") ||
                                error.includes("Email"),
                            ),
                          })}
                        />

                        <div className="w-32">
                          {groupUuid ? (
                            <GroupRoleSelection
                              value={user.groupRole}
                              onChange={(groupRole) =>
                                updateUser(user.id, { groupRole })
                              }
                              disabled={isProcessing}
                            />
                          ) : (
                            <UserRoleSelection
                              value={user.role}
                              onChange={(role) => updateUser(user.id, { role })}
                              disabled={isProcessing}
                            />
                          )}
                        </div>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeUser(user.id)}
                          disabled={users.length === 1 || isProcessing}
                          className={cn({ "row-span-2": !groupUuid })}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>

                        {!groupUuid && (
                          <div className="col-span-2">
                            <GroupChipCombobox
                              selectedGroupUuids={user.groupUuids}
                              onChange={(value: string, selected: boolean) =>
                                handleGroupSelection(
                                  user.id,
                                  value,
                                  selected,
                                  users,
                                  updateUser,
                                )
                              }
                            />
                          </div>
                        )}
                      </div>

                      {progress && (
                        <div className="text-sm">
                          <Row className="items-center gap-2">
                            <span
                              className={cn({
                                "text-red-600": progress.status === "failed",
                              })}
                            >
                              {progress.status === "pending" && "Pending..."}
                              {progress.status === "creating" &&
                                "Creating user..."}
                              {progress.status === "completed" &&
                                "Created successfully"}
                              {progress.status === "failed" && "Failed"}
                            </span>
                          </Row>
                          {progress.error && (
                            <p className="mt-1 text-xs text-red-600">
                              {progress.error}
                            </p>
                          )}
                        </div>
                      )}

                      {!progress && hasUserErrors && (
                        <div className="text-sm text-red-600">
                          {userErrors.map((error) => (
                            <p key={error} className="text-xs">
                              {error}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </AlertDialogDescription>

        <AlertDialogFooter>
          <div className="flex w-full items-center justify-between">
            <div className="flex gap-2">
              {wizard.canPrev && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={wizard.prev}
                  disabled={isProcessing}
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                  Back
                </Button>
              )}
            </div>

            <div className="flex gap-2">
              <AlertDialogCancel>Cancel</AlertDialogCancel>

              {wizard.currentStep === "input" && (
                <Button
                  size="sm"
                  onClick={wizard.next}
                  disabled={!wizard.canNext}
                >
                  Next
                  <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                </Button>
              )}

              {wizard.currentStep === "create" && (
                <AlertDialogAction
                  onClick={handleCreate}
                  disabled={isProcessing || hasErrors || !hasValidUsers}
                >
                  {isProcessing
                    ? "Creating..."
                    : `Create ${users.length} User${users.length > 1 ? "s" : ""}`}
                </AlertDialogAction>
              )}
            </div>
          </div>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
